/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { createContext, ReactNode, useContext, useCallback, useState, useRef, MutableRefObject } from 'react';

import useWindowDimensions from '../common/hooks/useWindowDimensions';

type rootValue = {
  navBarOpen: boolean;
  setNavBarOpen: (boolean) => void;
  scrollRef: MutableRefObject<HTMLDivElement>;
  scrollToElement: () => void;
  closeNavIfMobile: () => void;
};

const RootContext = createContext<rootValue | null>(null);

const useRootContext = () => {
  const rootContext = useContext(RootContext);
  if (rootContext === undefined) {
    // throwing this error helps in development when the context is out of scope.
    throw new Error('rootContext must be inside a RootProvider');
  }
  return rootContext;
};

type rootProviderProps = {
  children: ReactNode;
};

const RootProvider = ({ children }: rootProviderProps): JSX.Element => {
  const { isMobile } = useWindowDimensions();
  const [navBarOpen, setNavBarOpen] = useState(!isMobile);
  const scrollRef = useRef(null);
  const scrollToElement = useCallback(() => scrollRef.current.scrollIntoView(), [scrollRef]);

  const closeNavIfMobile = useCallback(() => {
    if (isMobile) setNavBarOpen(false);
  }, [isMobile, setNavBarOpen]);

  return (
    <RootContext.Provider
      value={{
        navBarOpen,
        setNavBarOpen,
        scrollRef,
        scrollToElement,
        closeNavIfMobile,
      }}
    >
      {children}
    </RootContext.Provider>
  );
};

export { RootProvider, useRootContext };
